import React, { useMemo, useState } from 'react';
import { combineClasses } from '../../utils/formatters';

const NewServiceCard = ({ service, onClick, onBookNow, onLearnMore, className = '' }) => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  const visibleFeatures = useMemo(() => {
    if (!Array.isArray(service.features)) {
      return [];
    }
    return showAllFeatures ? service.features : service.features.slice(0, 2);
  }, [service.features, showAllFeatures]);

  const hasMoreFeatures = Array.isArray(service.features) && service.features.length > 2;

  const formatPrice = (value) => {
    const numeric = Number(value || 0);
    return `₹${numeric.toLocaleString('en-IN')}`;
  };

  const handleClick = () => {
    if (onClick) onClick(service);
  };

  const handleBookNow = (e) => {
    e.stopPropagation();
    if (onBookNow) {
      onBookNow(service);
    }
  };

  const handleLearnMore = (e) => {
    e.stopPropagation();
    if (onLearnMore) {
      onLearnMore(service);
    } else if (onClick) {
      onClick(service);
    }
  };

  const handleToggleFeatures = (e) => {
    e.stopPropagation();
    setShowAllFeatures((prev) => !prev);
  };

  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'TRENDING':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'POPULAR':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'PREMIUM':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'FAST':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div 
      className={combineClasses(
        "bg-white rounded-xl border border-gray-200 overflow-hidden",
        "hover:shadow-xl cursor-pointer group h-full flex flex-col",
        "transition-all duration-300 ease-in-out",
        "hover:scale-105 hover:border-orange-200",
        "relative w-full",
        className
      )}
      onClick={handleClick}
    >
      {/* NEW Badge */}
      <div className="absolute top-2 sm:top-3 left-2 sm:left-3 z-10">
        <span className="bg-green-100 text-green-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium border border-green-200">
          NEW
        </span>
      </div>

      {/* Service Badge */}
      {service.badge && (
        <div className="absolute top-2 sm:top-3 right-2 sm:right-3 z-10">
          <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium border ${getBadgeColor(service.badge)}`}>
            {service.badge}
          </span>
        </div>
      )}

      {/* Service Icon with Enhanced Background */}
      <div className="bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 p-4 sm:p-6 text-center relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100/30 to-cyan-100/30"></div>
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-4 left-4 w-8 h-8 bg-white/40 rounded-full"></div>
          <div className="absolute bottom-4 right-4 w-6 h-6 bg-white/30 rounded-full"></div>
          <div className="absolute top-1/2 right-6 w-4 h-4 bg-white/20 rounded-full"></div>
        </div>
        
        <div className="relative z-10">
          <div className="text-3xl sm:text-4xl lg:text-5xl mb-2 sm:mb-3 group-hover:scale-110 transition-transform duration-300">
            {service.icon}
          </div>
          <div className="flex items-center justify-center space-x-2 text-xs sm:text-sm text-gray-600">
            <span className="flex items-center space-x-1">
              <span className="text-yellow-500">⭐</span>
              <span className="font-medium">{service.rating}</span>
            </span>
            <span>•</span>
            <span className="hidden sm:inline">{service.reviewCount} reviews</span>
            <span className="sm:hidden">{service.reviewCount}</span>
          </div>
        </div>
      </div>

      {/* Enhanced Service Details */}
      <div className="p-3 sm:p-4 lg:p-5 flex-1 flex flex-col">
        {/* Category and Duration */}
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          <span className="bg-blue-100 text-blue-700 px-2 sm:px-3 py-1 rounded-full text-xs font-medium">
            {service.category}
          </span>
          <span className="text-xs text-gray-500 flex items-center">
            <span className="mr-1">⏱️</span>
            <span className="truncate">{service.duration}</span>
          </span>
        </div>

        {/* Service Name */}
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 leading-tight group-hover:text-blue-600 transition-colors">
          {service.name}
        </h3>

        {/* Service Description */}
        <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 line-clamp-2 leading-relaxed">
          {service.detailedDescription || service.description}
        </p>

        {/* Features */}
        {service.features && service.features.length > 0 && (
          <div className="space-y-1 mb-3 sm:mb-4">
            {visibleFeatures.map((feature, index) => (
              <div key={index} className="flex items-center space-x-2 text-xs text-gray-600">
                <span className="text-blue-500">✓</span>
                <span className="truncate">{feature}</span>
              </div>
            ))}
            {hasMoreFeatures && (
              <button
                type="button"
                onClick={handleToggleFeatures}
                className="text-xs text-blue-600 hover:text-blue-700 mt-1 font-medium"
              >
                {showAllFeatures
                  ? 'Show less features'
                  : `+${service.features.length - 2} more features`}
              </button>
            )}
          </div>
        )}

        {/* Price Section */}
        <div className="flex items-center justify-between mb-3 sm:mb-4 py-2 sm:py-3 border-t border-gray-100">
          <div>
            <div className="text-lg sm:text-xl font-bold text-gray-900">
              {formatPrice(service.price)}
            </div>
            {service.originalPrice && service.originalPrice !== service.price && (
              <div className="flex items-center space-x-2">
                <span className="text-xs sm:text-sm text-gray-500 line-through">
                  {formatPrice(service.originalPrice)}
                </span>
                <span className="text-xs text-green-600 font-medium">
                  {Math.round(((service.originalPrice - service.price) / service.originalPrice) * 100)}% OFF
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mt-auto">
          <button
            onClick={handleLearnMore}
            className="px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium"
          >
            Learn More
          </button>
          <button
            onClick={handleBookNow}
            className="px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-xs sm:text-sm font-medium"
          >
            Book Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewServiceCard;
